import { Injectable } from '@angular/core';

export interface EventRsvpIntent {
  eventId: number;
  canonicalPath: string;
  requestedAt: number;
}

@Injectable({ providedIn: 'root' })
export class EventRsvpIntentService {
  private readonly key = 'citizex:rsvp-intent:v1';
  private readonly ttlMs = 30 * 60 * 1000;

  save(eventId: number, canonicalPath: string): void {
    if (!this.isCanonicalEventPath(eventId, canonicalPath)) {
      return;
    }
    this.write({ eventId, canonicalPath, requestedAt: Date.now() });
  }

  peek(): EventRsvpIntent | null {
    const intent = this.read();
    const now = Date.now();
    if (
      !intent ||
      intent.requestedAt > now ||
      now - intent.requestedAt > this.ttlMs
    ) {
      this.clear();
      return null;
    }
    return intent;
  }

  matchesFor(eventId: number, canonicalPath: string): boolean {
    const intent = this.peek();
    if (
      !intent ||
      intent.eventId !== eventId ||
      intent.canonicalPath !== canonicalPath
    ) {
      return false;
    }
    return true;
  }

  consumeFor(eventId: number, canonicalPath: string): boolean {
    if (!this.matchesFor(eventId, canonicalPath)) return false;
    this.clear();
    return true;
  }

  clear(): void {
    try {
      localStorage.removeItem(this.key);
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }

  private read(): EventRsvpIntent | null {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return null;
      const value = JSON.parse(raw) as Partial<EventRsvpIntent>;
      return Number.isSafeInteger(value.eventId) &&
        typeof value.canonicalPath === 'string' &&
        this.isCanonicalEventPath(value.eventId, value.canonicalPath) &&
        typeof value.requestedAt === 'number'
        ? (value as EventRsvpIntent)
        : null;
    } catch {
      return null;
    }
  }

  private write(intent: EventRsvpIntent): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(intent));
    } catch {
      // An unavailable local store simply prevents post-auth resume; it never
      // turns a page view into a write.
    }
  }

  private isCanonicalEventPath(
    eventId: unknown,
    canonicalPath: unknown
  ): eventId is number {
    if (!Number.isSafeInteger(eventId) || typeof canonicalPath !== 'string')
      return false;
    const match = /^\/events\/(\d+)(?:\/[^/?#]+)?$/.exec(canonicalPath);
    return match !== null && Number(match[1]) === eventId;
  }
}
